<script setup lang="ts">
import type { AxiosError } from 'axios'
import type { TermsItem } from '@store'

definePageMeta({
  layout: 'one-column-full-height',
})

const { gtEvent } = useGoogleTag()
const route = useRoute()
const etcStore = useEtcStore()
const activeTermId = ref(route.params.id as TermsItem['stplatId'])
const menuOpen = ref(false)
const err = ref<AxiosError>()

/**
 * 페이지 마운트
 */
onMounted(() => {
  handleOnLoadTerm(activeTermId.value, '', true)
})

/**
 * Server Side API Request
 */
const { data: term } = await useAsyncData('term', async () => {
  try {
    const result = await etcStore.fetchTerm({
      stplatId: String(route.params.id),
    })
    return Promise.resolve(result)
  } catch (_err) {
    err.value = _err as AxiosError
    return Promise.reject(_err)
  }
})

if (term.value) {
  useSetSEO({
    useHeadArgs: [
      {
        title: term.value.stplatNm,
        meta: [
          {
            name: 'description',
            content: useGetMaxThreeParagraphTexts(term.value.stplatDetailCn),
          },
        ],
      },
    ],
    useSeoMetaArgs: [
      {
        ogTitle: term.value.stplatNm,
        ogDescription: useGetMaxThreeParagraphTexts(term.value.stplatDetailCn),
        ogImage: useGetFirstImageSource(term.value.stplatDetailCn),
      },
    ],
  })
}

if (err.value && process.server) {
  const status = (err.value as any).response.status

  switch (status) {
    // 해당 글을 찾을 수 없습니다.
    case 2011:
      throw createError({
        statusCode: 404,
        statusMessage: `term ${route.params.id} is not find`,
      })
    default:
      throw createError({
        statusCode: status,
        statusMessage: `term ${route.params.id} is error`,
      })
  }
}

const handleOnLoadTerm = (
  termId: TermsItem['stplatId'],
  label: string,
  mounted: boolean
) => {
  // route.path !== `/board/${path}`
  if (!mounted && route.path !== `/terms/${termId}`) {
    gtEvent('clickEvent', {
      eventCategory: '클릭',
      eventAction: `정책 > 사이드 메뉴 > ${label}`,
      eventLabel: label,
      eventSlot: '정책',
      eventI18nAddr: '',
      eventComponent: 'Button',
    })
  }
  useNavigations({ external: false, url: `/terms/${termId}` })
}
</script>

<template>
  <div class="term-wrapper">
    <div class="left">
      <NovaBoxBase class="menu-area">
        <div class="menu-header">
          <h4 class="title">{{ $t('policy') }}</h4>
          <NovaButtonIcon
            :icon="{ type: 'outline', name: 'carrot-bottom' }"
            :size="20"
            :class="['btn-toggle', { on: menuOpen }]"
            @click="menuOpen = !menuOpen"
          />
        </div>
        <ul :class="['menu-list', { open: menuOpen }]">
          <li
            v-for="(termItem, index) in etcStore.terms"
            :key="index"
            :class="{ on: termItem.stplatId === activeTermId }"
            @click="
              handleOnLoadTerm(termItem.stplatId, termItem.stplatNm, false)
            "
          >
            {{ termItem.stplatNm }}
          </li>
        </ul>
      </NovaBoxBase>
    </div>

    <div class="term-content">
      <NovaBoxBase
        v-if="term"
        v-dompurify-html="term.stplatDetailCn"
        class="inner"
      />

      <NovaBoxBase v-if="err" class="inner">
        <NovaBoxEmptyMessage
          :message="$t('statusMessages.network.error')"
          :fill="true"
        />
      </NovaBoxBase>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.term-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  height: 100%;
  overflow: hidden;

  .left {
    flex-shrink: 0;
    max-width: 190px;
    width: 100%;
    padding: 20px 0 20px;

    .menu-area {
      gap: 10px;
      padding: 20px 0 20px;
      width: 100%;
      max-height: 100%;

      .menu-header {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 20px;

        .title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          @include text-style($text-heading3-bold);
        }

        .btn-toggle {
          display: none;
          color: $color-text-3;
          @include transition(transform 0.2s ease-in-out);

          &.on {
            transform: rotate(180deg);
          }
        }
      }

      .menu-list {
        display: flex;
        flex-direction: column;
        padding: 0 20px;
        width: 100%;
        height: auto;
        overflow-y: overlay;

        li {
          padding: 10px;
          @include text-style($text-body-14-regular);
          color: $color-text-2;
          border-radius: 6px;
          cursor: pointer;

          &.on {
            background-color: $color-bg-1;
          }
        }
      }
    }
  }

  .term-content {
    height: 100%;
    flex-grow: 1;
    padding: 20px 0 20px;

    > .inner {
      height: 100%;
      overflow-y: overlay;

      :deep(.footer_guide) {
        .h3_wrap {
          display: none;
        }

        h3 {
          display: none;
          padding-bottom: 2.5rem;
        }
      }

      :deep(.terms_box) {
        .agree_none {
          display: none;
        }
        &.date230313 {
          display: block;
        }
        &.date221125 {
          display: none;
        }
        &.date220630 {
          display: none;
        }
        .t_right.pt30 {
          text-align: right;
          font-size: 14px;
          padding-bottom: 10px;
        }
        .terms_box_list {
          > li {
            padding-top: 20px;
            &:first-child {
              padding-top: 0;
            }
            dl {
              dt {
                font-weight: 500;
                font-size: 16px;
                padding-bottom: 5px;
              }
              dd {
                font-size: 14px;
                line-height: 1.4em;
                ul {
                  padding-left: 0px;
                  li {
                    padding-top: 10px;
                    padding-left: 16px;
                    text-indent: -16px;
                    ol {
                    }
                  }
                }
                table {
                  width: 100%;
                  table-layout: fixed;
                  border: 1px solid #154f65;
                  th {
                    padding: 10px;
                    color: #fff;
                    text-align: center;
                    background: #8d8d8d;
                    vertical-align: middle;
                    border: 1px solid #154f65;
                  }
                  td {
                    padding: 10px;
                    text-align: center;
                    vertical-align: middle;
                    border: 1px solid #154f65;
                  }
                }
                div {
                  padding: 10px 0;
                }
              }
            }
          }
          & + p {
            padding-top: 10px;
            font-size: 14px;
          }
          & + p + p {
            padding-top: 10px;
            font-size: 14px;
          }
        }
      }

      :deep(.date_box) {
        padding-top: 20px;
        font-size: 14px;
        ul {
          li {
            font-size: 14px;
          }
        }
      }
    }
  }

  @include mobile {
    flex-direction: column;
    gap: 20px;
    padding: 15px 20px;

    .left {
      padding-top: 0;
      padding-bottom: 0;
      max-width: 100%;

      .menu-area {
        max-width: 100%;

        .menu-header {
          .btn-toggle {
            display: inline-flex;
          }
        }

        .menu-list {
          display: none;
          max-height: 200px;
        }

        .menu-list.open {
          display: block;
        }
      }
    }

    .term-content {
      padding-top: 0;
      padding-bottom: 0;
      width: 100%;
      overflow: hidden;
    }
  }
}
</style>
